// ── Ordinex — Web push opt-in (mobile Track A, wave 2) ────────────────────────
// The server side (subscription table, send transport, daily digests) lives in
// Supabase; this file is the device side: offer notifications at a calm
// moment, subscribe on an explicit yes, keep the subscription row fresh, and
// expose an off switch. Loaded on index.html after offline.js (it reuses the
// install-card look and the same "don't interrupt a fill" rules).
//
// iOS reality: PushManager only exists inside a home-screen-installed web app
// (16.4+). In a Safari tab the card simply never shows — the install invite
// (wave 3) is the path there, and after installing, this card appears.

// Public VAPID key — public by design (it identifies our push sender; the
// private half lives only in Supabase secrets).
const ORDINEX_VAPID_PUBLIC = 'BOO7P80opn_oYbQgARFhKiQdAfatkew3rqAAI1FXeZicMZma7HfFYn-QJRdqloh9VmGbNZ6cJmQoYfG8WCrNsWg';

const _PUSH_DISMISS_KEY = 'ordinexPushPromptDismissed';
let _pushCardShown = false;

function _pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}
function _pushDismissed() {
  try { return localStorage.getItem(_PUSH_DISMISS_KEY) === '1'; } catch (_) { return false; }
}

function _vapidKeyBytes() {
  const b64 = (ORDINEX_VAPID_PUBLIC + '='.repeat((4 - ORDINEX_VAPID_PUBLIC.length % 4) % 4))
    .replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Subscribe this device and store/refresh the row. Endpoint is the natural
// key — browsers rotate it, and upsert-on-endpoint makes that invisible.
async function _pushSubscribe() {
  // The admin panel never registers the worker itself — an admin-only user
  // who never opened the field app would hang on `.ready` without this.
  // register() is idempotent when the worker is already active.
  try { await navigator.serviceWorker.register('sw.js'); } catch (_) {}
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: _vapidKeyBytes(),
  });
  const j = sub.toJSON();
  const row = {
    user_id: window.currentUser.id,
    endpoint: sub.endpoint,
    p256dh: (j.keys && j.keys.p256dh) || '',
    auth: (j.keys && j.keys.auth) || '',
    user_agent: (navigator.userAgent || '').slice(0, 200),
  };
  if (window.currentOrg && window.currentOrg.id) row.org_id = window.currentOrg.id;
  const { error } = await sbClient.from('push_subscriptions').upsert(row, { onConflict: 'endpoint' });
  if (error) throw error;
  return sub;
}

// Off switch (used by tests/support today, a visible settings row later):
// drop the device subscription AND its server row.
async function ordinexDisablePush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try { await sbClient.from('push_subscriptions').delete().eq('endpoint', sub.endpoint); } catch (_) {}
      await sub.unsubscribe();
    }
  } catch (e) { console.warn('[push] disable failed:', e); }
}
window.ordinexDisablePush = ordinexDisablePush;

// Same calm-moment rules as the install card, plus: never both cards at once
// (install wins — a phone that hasn't installed can't receive iOS push anyway).
// The auto-card is FIELD-APP only (#homeScreen exists there); the admin panel
// gets its deliberate switch in the Notifications tab instead of a pop-up.
function _pushEligible() {
  if (!_pushSupported() || _pushDismissed() || _pushCardShown) return false;
  if (!document.getElementById('homeScreen')) return false;
  if (Notification.permission !== 'default') return false;
  if (!window.currentUser) return false;
  try { if (typeof state !== 'undefined' && state.submissionType) return false; } catch (_) {}
  if (document.getElementById('installCard')) return false;
  return true;
}

// ── Visible switch (PWA item 4) ───────────────────────────────────────────────
// One source of truth for the field user-menu row and the admin Notifications
// tab block. Status: 'on' | 'off' | 'blocked' | 'unsupported'.
async function ordinexPushStatus() {
  if (!_pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'off';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && await reg.pushManager.getSubscription();
    return sub ? 'on' : 'off';
  } catch (_) { return 'off'; }
}

async function ordinexPushRefreshUI() {
  const status = await ordinexPushStatus();
  const LABEL = {
    on: 'Notifications: On', off: 'Notifications: Off',
    blocked: 'Notifications blocked in browser', unsupported: '',
  };
  // Field user menu row.
  const menuBtn = document.getElementById('userMenuPushBtn');
  if (menuBtn) {
    menuBtn.style.display = status === 'unsupported' ? 'none' : '';
    const lbl = document.getElementById('userMenuPushLabel');
    if (lbl) lbl.textContent = LABEL[status];
  }
  // Admin Notifications tab block.
  const statusEl = document.getElementById('notifDeviceStatus');
  const btn = document.getElementById('notifDeviceBtn');
  if (statusEl) {
    statusEl.textContent =
      status === 'on' ? 'On — expiring certifications and overdue actions reach this device even when the tab is closed.' :
      status === 'off' ? 'Off — this device gets nothing until you turn it on.' :
      status === 'blocked' ? 'Blocked in the browser — allow notifications for app.ordinex.ca in the browser\'s site settings, then come back.' :
      'Not supported in this browser. On iPhone, install the app to the home screen first.';
  }
  if (btn) {
    btn.style.display = (status === 'blocked' || status === 'unsupported') ? 'none' : '';
    btn.textContent = status === 'on' ? 'Turn off' : 'Turn on';
  }
  return status;
}
window.ordinexPushRefreshUI = ordinexPushRefreshUI;

async function ordinexTogglePush() {
  const status = await ordinexPushStatus();
  try {
    if (status === 'on') {
      await ordinexDisablePush();
      showToast('Notifications are off for this device.', 'info');
    } else if (status === 'off') {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        await _pushSubscribe();
        showToast('Notifications are on for this device.', 'success');
      }
    } else if (status === 'blocked') {
      showToast('Notifications are blocked — allow them in the browser\'s site settings.', 'warning');
    }
  } catch (e) {
    console.warn('[push] toggle failed:', e);
    showToast('Could not change notifications.', 'error');
  }
  return ordinexPushRefreshUI();
}
window.ordinexTogglePush = ordinexTogglePush;

function _maybeShowPushCard() {
  if (!_pushEligible()) return;
  _pushCardShown = true;
  const card = document.createElement('div');
  card.id = 'pushCard';
  card.className = 'install-card';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', 'Turn on notifications');
  card.innerHTML = `
    <span class="install-card-icon install-card-icon--bell"><i data-lucide="bell-ring"></i></span>
    <div class="install-card-text">
      <b>Get alerts on this device</b>
      <span>Expiring certifications and overdue actions — even when the app is closed.</span>
    </div>
    <div class="install-card-actions">
      <button type="button" class="install-card-btn" id="pushCardGo">Turn on</button>
      <button type="button" class="install-card-dismiss" id="pushCardNo" aria-label="Not now">Not now</button>
    </div>`;
  document.body.appendChild(card);
  try { if (window.lucide) lucide.createIcons(); } catch (_) {}
  document.getElementById('pushCardNo').onclick = () => {
    try { localStorage.setItem(_PUSH_DISMISS_KEY, '1'); } catch (_) {}
    card.remove();
  };
  document.getElementById('pushCardGo').onclick = async () => {
    card.remove();
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { _pushCardShown = false; return; }
      await _pushSubscribe();
      try { localStorage.setItem(_PUSH_DISMISS_KEY, '1'); } catch (_) {}
      showToast('Notifications are on for this device.', 'success');
    } catch (e) {
      console.warn('[push] subscribe failed:', e);
      showToast('Could not turn on notifications.', 'error');
      _pushCardShown = false;
    }
  };
}

function _initPush() {
  if (!_pushSupported()) return;
  // Already opted in on this device → quietly refresh the row on boot
  // (endpoints rotate; last_used_at doubles as a liveness signal).
  const refresh = async () => {
    if (Notification.permission !== 'granted' || !window.currentUser) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await _pushSubscribe();
    } catch (_) { /* next boot retries */ }
  };
  setTimeout(refresh, 5000);
  setTimeout(ordinexPushRefreshUI, 1500);   // paint the menu/tab switch state
  // The offer: a touch later than the install card so they never collide.
  let tries = 0;
  const t = setInterval(() => {
    if (++tries > 40 || _pushCardShown || _pushDismissed() || Notification.permission !== 'default') { clearInterval(t); return; }
    _maybeShowPushCard();
  }, 4000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _initPush);
} else {
  _initPush();
}
